/**
 * Composition root for the deployment PoC.
 *
 * The app itself is in `vendor/` — a **verbatim** copy of
 * `examples/embedded-app` from the app-bridge repository, so what we deploy and
 * prove is exactly what third-party developers are told to write. CI fails if
 * the copy drifts; `bun run sync:vendor` refreshes it.
 *
 * This file is the delta a real third party would also have to write for
 * themselves: where the platform origins come from, and whatever instrumentation
 * they want. Ours adds two things the reference deliberately does not have —
 * origin resolution with a preview fallback, and a bridge message log.
 */

import { connect } from '@narrative.io/app-bridge/guest'
import { renderConnecting, renderNotEmbedded, runEmbedded } from '../vendor/app.js'
import { el } from '../vendor/dom.js'
import { currentOriginResolution } from './config.js'
import { MessageLog } from './log.js'
import { mountLogOverlay } from './log-overlay.js'

const root = document.getElementById('app') as HTMLElement
const resolution = currentOriginResolution()
const log = new MessageLog()

const connecting = connect({
	platformOrigin: resolution.origins,
	// Instrumentation the reference example has no reason to carry: it exists so
	// a reviewer can watch every message crossing the postMessage boundary.
	onLogEntry: log.append,
})

mountLogOverlay(log)
renderConnecting(root, resolution.origins)

connecting.then(
	(bridge) => {
		runEmbedded(root, bridge)
		if (resolution.unsafe) {
			// Loud on purpose. This build accepted a handshake from whoever framed
			// it, which is the practice the library's security model tells app
			// authors never to use — acceptable only because this app holds nothing
			// worth stealing and its preview origins are unknown at build time.
			root.querySelector('#main')?.prepend(
				el(`<div class="banner banner-warn">
					<strong>Preview build.</strong> Platform origins were taken from
					<code>ancestorOrigins</code> — this app trusted whichever page framed it.
					Production builds pin their origins. Never do this in a real app.
				</div>`),
			)
		}
	},
	(error) => renderNotEmbedded(root, error, resolution.origins),
)
